import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

/** Lightweight float animation (no @react-three/drei to avoid 504 / antivirus issues) */
function SimpleFloat({ children, speed = 2 }: { children: React.ReactNode; speed?: number }) {
  const ref = useRef<THREE.Group>(null);
  const t = useRef(0);
  useFrame((_, delta) => {
    t.current += delta;
    if (ref.current) {
      ref.current.position.y = Math.sin(t.current * speed) * 0.06;
      ref.current.rotation.x = Math.sin(t.current * speed * 0.7) * 0.04;
      ref.current.rotation.z = Math.sin(t.current * speed * 0.5) * 0.04;
    }
  });
  return <group ref={ref}>{children}</group>;
}

function FloatingShape({ position, color, shape = 'sphere', speed = 1 }: { position: [number, number, number]; color: string; shape?: 'sphere' | 'torus' | 'box'; speed?: number }) {
  const ref = useRef<THREE.Mesh>(null);
  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 0.2 * speed;
  });
  const geometry = shape === 'torus' ? <torusGeometry args={[0.4, 0.15, 16, 32]} /> : shape === 'box' ? <boxGeometry args={[0.6, 0.6, 0.6]} /> : <sphereGeometry args={[0.5, 32, 32]} />;
  return (
    <SimpleFloat speed={2}>
      <mesh ref={ref} position={position}>
        {geometry}
        <meshStandardMaterial color={color} roughness={0.4} metalness={0.1} />
      </mesh>
    </SimpleFloat>
  );
}

function GradientBlob() {
  const ref = useRef<THREE.Mesh>(null);
  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 0.08;
  });
  return (
    <mesh ref={ref} position={[0, 0, -3]} scale={[3, 3, 3]}>
      <sphereGeometry args={[1, 32, 32]} />
      <meshStandardMaterial color="#7dd3fc" transparent opacity={0.16} roughness={0.8} metalness={0} />
    </mesh>
  );
}

/** Blue/teal/slate 3D scene for the therapist landing – dark theme to match page. */
export function Scene3DTherapist() {
  return (
    <>
      <color attach="background" args={['#0f172a']} />
      <ambientLight intensity={0.6} />
      <directionalLight position={[10, 10, 5]} intensity={1.2} />
      <pointLight position={[-5, -5, 5]} intensity={0.5} color="#7dd3fc" />
      <pointLight position={[5, 0, 3]} intensity={0.4} color="#5eead4" />
      <FloatingShape position={[-1.8, 0.4, -0.5]} color="#38bdf8" shape="sphere" speed={0.8} />
      <FloatingShape position={[1.6, -0.3, -0.6]} color="#2dd4bf" shape="torus" speed={1.2} />
      <FloatingShape position={[-0.6, -0.8, -0.4]} color="#64748b" shape="box" speed={0.6} />
      <FloatingShape position={[1.2, 0.7, -0.5]} color="#a7f3d0" shape="sphere" speed={1} />
      <FloatingShape position={[-1.2, 0.9, -0.7]} color="#fbbf24" shape="torus" speed={0.7} />
      <GradientBlob />
    </>
  );
}
